import { QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { queryClient } from '@/api/client'
import { configureApiClient } from '@/api/setup'
import { InAppBrowserScreen } from '@/components/screens/InAppBrowserScreen'
import { LoadingScreen } from '@/components/screens/LoadingScreen'
import { AuthProvider } from '@/context/AuthContext'
import { CookieConsentProvider } from '@/context/CookieConsentContext'
import { UnsavedChangesProvider } from '@/context/UnsavedChangesContext'
import { usePrefetchPages } from '@/hooks/usePrefetchPages'
import { initAmplitudeIfAllowed } from '@/lib/analytics'
import { getApiBaseUrl } from '@/lib/env'
import { detectInAppBrowser, alreadyAttemptedRedirect, redirectToNativeBrowser } from '@/lib/inAppBrowser'
import { initSentry } from '@/lib/sentry'
import router from '@/routes/router'

import './reset.css'
import './styles.css'

// Wire the shared API-client mutator before any React Query hook fires.
configureApiClient()

// Add preconnect for API base URL if configured
const apiBaseUrl = getApiBaseUrl()
if (apiBaseUrl) {
  const link = document.createElement('link')
  link.rel = 'preconnect'
  link.href = apiBaseUrl
  document.head.appendChild(link)
}

initSentry()

initAmplitudeIfAllowed()

function App() {
  // Prefetch all pages in parallel after initial render
  usePrefetchPages()

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Suspense fallback={<LoadingScreen />}>
          <CookieConsentProvider>
            <UnsavedChangesProvider>
              {/*
                v7_startTransition wraps every router state update in React.startTransition,
                making navigations React transitions. Navigating into a lazy dark route
                (e.g. the post-roleplay navigate() into the lazy SessionFeedback page) then
                keeps the current dark view on screen until the chunk resolves and commits
                the next route's layout effects before paint — so neither the page
                background nor the sidebar/header chrome flashes light (the "white flash").
                A per-navigate startTransition(navigate) wrap can't achieve this in RR v6:
                the router notifies subscribers asynchronously, so the setState lands
                outside the wrap's scope; the flag wraps it at the source instead.
              */}
              <RouterProvider router={router} />
            </UnsavedChangesProvider>
          </CookieConsentProvider>
        </Suspense>
      </AuthProvider>
    </QueryClientProvider>
  )
}

const inAppInfo = detectInAppBrowser()
if (inAppInfo && !alreadyAttemptedRedirect()) {
  redirectToNativeBrowser(inAppInfo.platform)
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {inAppInfo ? <InAppBrowserScreen platform={inAppInfo.platform} app={inAppInfo.app} /> : <App />}
  </StrictMode>,
)
